import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useTemporaryStorage } from '#/hooks/useTemporaryStorage';
import { cmsApiSubscribeToSubsite } from '#/modules/cms/api/cmsApiSubscribeToSubsite';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import { userDashboardApiCreateSavedListing } from '#/modules/userDashboard/api/savedListing/userDashboardApiCreateSavedListing';
import { userDashboardSaveSearch } from '#/modules/userDashboard/zustand-stores/userDashboardSavedSearchesStore';
import { getFirstParam } from '#/utils/url/getFirstParam';
import { getRelativeURL } from '#/utils/url/getRelativeURL';
import { legacyIsExternalURL } from '#/utils/url/isExternalURL';
import { showModal } from '#/zustand-stores/modalStore';

export function useTemporaryStorageHandler() {
  const navigate = useNavigate();
  const [navigated, setNavigated] = useState(false);
  const { showOrgSavedToast } = useOrgSavedToast();
  const { markActionsWhileLoggedOutProcessed } = useUserEnvironmentActions();

  const tmpId =
    typeof window !== 'undefined'
      ? getFirstParam(window.location, 'tmpId')
      : '';

  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [saveTemporaryStorage, temporaryStorage, loaded] =
    useTemporaryStorage(tmpId);

  useEffect(() => {
    const promises: Promise<unknown>[] = [];
    if (
      'redirectUrl' in temporaryStorage &&
      typeof temporaryStorage.redirectUrl === 'string'
    ) {
      setRedirectUrl(temporaryStorage.redirectUrl);
    }

    if ('savedListing' in temporaryStorage) {
      promises.push(
        userDashboardApiCreateSavedListing(
          temporaryStorage.savedListing.id,
          temporaryStorage.savedListing.type,
        ).then(() => {
          if (temporaryStorage.savedListing.type === 'ORG') {
            showOrgSavedToast();
          }
          return;
        }),
      );
    }

    if ('subscribedSubsite' in temporaryStorage) {
      promises.push(
        cmsApiSubscribeToSubsite(
          temporaryStorage.subscribedSubsite.subscribedSubsiteId,
        ).then(() => showModal('SUBSCRIBED_TO_SUBSITE', {})),
      );
    }

    if ('savedSearch' in temporaryStorage) {
      promises.push(userDashboardSaveSearch(temporaryStorage.savedSearch));
    }

    if (promises.length > 0) {
      void Promise.all(promises).then(() => {
        markActionsWhileLoggedOutProcessed();
        return;
      });
    }
  }, [
    markActionsWhileLoggedOutProcessed,
    showOrgSavedToast,
    temporaryStorage,
    tmpId,
  ]);

  if (redirectUrl && !navigated) {
    if (legacyIsExternalURL(redirectUrl)) {
      window.location.href = redirectUrl;
    } else {
      void navigate(getRelativeURL(redirectUrl), { replace: true });
    }
    setNavigated(true);
  }

  return { temporaryStorage, saveTemporaryStorage, loaded };
}
